import { getStore } from "store";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import Async from "utils/Async";
import Constants from "const/Constants";
import Environment from "utils/Environment";
import Errors from "const/Errors";
import HttpStatuses from "const/HttpStatuses";
import MainApiActions from "actions/MainApiActions";
import RestApi from "api/RestApi";
import Urls from "utils/Urls";

const NOTIFICATION_AUTO_CLOSE_DELAY = 300000;

const ERROR_MESSAGE_LENGTH_LIMIT = 100;

const JSON_PRETTIFY_TAB_SIZE = 2;

export default class MainApi extends RestApi {
  static requests = {};

  token = null;

  fatalError = false;

  sequentialErrorsCount = 0;

  async getBlob(path, urlParams) {
    const headers = { Authorization: `Bearer ${this.token}` };

    const response = await fetch(
      `${this.apiUrl}${path}${urlParams ? `?${Urls.objectToQueryString(urlParams)}` : ""}`,
      { headers }
    );

    if (response.ok) {
      const responseBlob = await response.blob();

      return responseBlob;
    }

    return null;
  }

  makeRequest(method, path, urlParams, payload, fileField) {
    const requestKey = method + path;

    const requestUuid = uuid();

    const requestPromise = (async() => {
      const concurrentRequestPromises = Object.values(MainApi.requests)
        .filter(([concurrentRequestKey]) => concurrentRequestKey === requestKey)
        .map(([, concurrentRequestPromise]) => concurrentRequestPromise);

      if (concurrentRequestPromises.length) await Promise.all(concurrentRequestPromises);

      let result;

      let errorCode;

      if (this.fatalError) await Async.waitForever();
      try {
        if (this.sequentialErrorsCount === Constants.MAX_API_SEQUENTIAL_ERRORS) throw Errors.TOO_MANY_ERRORS;
        if (!this.token) throw Errors.SESSION_EXPIRED;

        const headers = { Authorization: `Bearer ${this.token}` };

        let requestData;

        if (fileField) {
          requestData = new FormData();
          requestData.append(fileField, payload);
        } else {
          requestData = JSON.stringify(payload);
          headers["Content-Type"] = "application/json";
        }

        const searchString = urlParams ? Urls.objectToQueryString(urlParams) : null;

        const response = await fetch(
          `${this.apiUrl}${path}${searchString ? `?${searchString}` : ""}`,
          {
            method,
            headers,
            body: requestData
          }
        );

        if (response.status === HttpStatuses.UNAUTHORIZED) throw Errors.SESSION_EXPIRED;

        let responseJson;

        if (response.ok) {
          responseJson = await response.json();
          if (!Object.keys(responseJson).length) responseJson = { ok: true };
        }
        if (!response.ok) {
          const developmentMode = Environment.checkIsStagingEnv() || Environment.checkIsDevMode();

          let errorJson;

          if (response.status === HttpStatuses.BAD_REQUEST || developmentMode) {
            try {
              errorJson = await response.json();
            } catch (error) {
              errorJson = null;
            }
          }
          if (developmentMode && errorJson?.message) {
            toast.dark(
              `DEV MODE ERROR: ${errorJson.message.substr(0, ERROR_MESSAGE_LENGTH_LIMIT)}`,
              {
                autoClose: NOTIFICATION_AUTO_CLOSE_DELAY,
                onClick: () => {
                  const { clipboard } = window.navigator;

                  if (clipboard && clipboard.writeText) {
                    clipboard.writeText(JSON.stringify(errorJson, null, JSON_PRETTIFY_TAB_SIZE));
                  }
                }
              }
            );
          }
          if (response.status === HttpStatuses.BAD_REQUEST
            && errorJson?.code === Errors.DOCUMENT_LINE_ITEMS_AMOUNT_MISMATCH) {
            errorCode = Errors.DOCUMENT_LINE_ITEMS_AMOUNT_MISMATCH;
          }

          throw Errors.REQUEST_ERROR;
        }
        this.sequentialErrorsCount = 0;
        result = responseJson;
      } catch (error) {
        const { dispatch } = getStore();

        let errorType = Errors.REQUEST_ERROR;

        if (error === Errors.SESSION_EXPIRED || error === Errors.TOO_MANY_ERRORS) {
          errorType = error;
          this.fatalError = true;
        }
        this.sequentialErrorsCount = errorCode ? 0 : this.sequentialErrorsCount + 1;
        dispatch(MainApiActions.apiErrorOccurred(errorType, this.fatalError));
        if (this.fatalError) await Async.waitForever();
        result = { ok: false, errorType, ...(errorCode ? { errorCode } : {}) };
      }
      delete MainApi.requests[requestUuid];

      return result;
    })();

    MainApi.requests[requestUuid] = [requestKey, requestPromise];

    return requestPromise;
  }
}
